<script setup lang="ts">
import UiLightbox from '~/components/Ui/lightbox/UiLightbox.vue'
import { useLightbox } from '~/composables/useLightbox'
import type { ProductLineDetail } from '~/types/api'

const props = defineProps<{
  line: ProductLineDetail | null
  setNid?: number | null
}>()

const { getResourceUrl, getImageSrcSet } = useResources()

const itemsPerPage = ref(5)
const page = ref(1)

const initialLoad = ref(true)
const isSwitchedToSet = ref(false)
const initialPaginationLoad = ref<boolean | null>(null)
let {
  data: images,
  status,
  refresh,
} = await useLazyAsyncData(
  props.setNid
    ? () =>
        createProductSetsRepository().getAllImagesPagination(
          page.value,
          itemsPerPage.value,
          props.setNid!
        )
    : () =>
        createProductLineRepository().getAllImagesPagination(
          page.value,
          itemsPerPage.value,
          props.line?.nid
        ),
  {
    default: defaultPaginationObject,
  }
)
if (images.value?.itemCount === 0 && props.setNid && !isSwitchedToSet.value) {
  isSwitchedToSet.value = true
  await loadSetImages()
}
watch(
  () => status.value,
  async () => {
    if (status.value === 'pending' && initialPaginationLoad.value === null) {
      initialPaginationLoad.value = true
    } else if (
      status.value !== 'pending' &&
      initialPaginationLoad.value === true
    ) {
      if (images.value.itemCount === 0 && !isSwitchedToSet.value) {
        isSwitchedToSet.value = true
        await loadSetImages()
      }
      initialPaginationLoad.value = false
    } else if (status.value !== 'pending') {
      initialLoad.value = false
    }
  }
)

const open = ref(false)
const galleryElement = ref<HTMLElement | null>(null)

async function loadSetImages() {
  const {
    data: lineImages,
    status: lineStatus,
    refresh: lineRefresh,
  } = await useLazyAsyncData(
    () =>
      createProductLineRepository().getAllImagesPagination(
        page.value,
        itemsPerPage.value,
        props.line?.nid
      ),
    {
      default: defaultPaginationObject,
    }
  )
  images = lineImages
  status = lineStatus
  refresh = lineRefresh
}

async function toggleOpen() {
  if (!open.value) {
    initialPaginationLoad.value = null
    itemsPerPage.value = 250
    refresh()
    open.value = true
  } else {
    itemsPerPage.value = 5
    if (page.value !== 1) {
      page.value = 1
    } else {
      await refresh()
    }
    open.value = false
    if (galleryElement.value) {
      scrollToElementWithOffset(galleryElement.value, 150)
    }
  }
}

watch(
  () => page.value,
  () => {
    refresh()
    if (galleryElement.value) {
      scrollToElementWithOffset(galleryElement.value, 150)
    }
  }
)
const loading = computed(() => {
  return (
    status.value === 'pending' ||
    initialLoad.value ||
    initialPaginationLoad.value
  )
})
const { visible, index, showImg } = useLightbox()
</script>

<template>
  <UiSection
    v-if="line?.videos?.length || images.items.length || loading"
    id="gallery"
    :title="$t('base.products.tabs.gallery')"
  >
    <div ref="galleryElement">
      <div v-if="status === 'pending'">
        <div
          class="grid grid-cols-2 items-center gap-5 sm:grid-cols-3 md:grid-cols-3 xl:grid-cols-4 2xl:xl:grid-cols-5"
        >
          <div
            v-for="i in 12"
            :key="'gallery-skeleton-' + i"
            class="flex w-full items-center justify-center"
          >
            <div class="aspect-gallery h-[125px] md:h-[150px] lg:h-[200px]">
              <UiSkeletonImage />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="line?.videos?.length > 0" class="container">
        <div class="flex flex-wrap justify-center">
          <div
            v-for="video in line?.videos ?? []"
            :key="video.nid"
            class="w-full px-5 md:w-1/2"
            style="aspect-ratio: 16 / 9"
          >
            <UiYoutubePlayer
              :video-id="video.videoId"
              :poster="video.posterImage?.url ?? ''"
            />
          </div>
        </div>
      </div>
      <div v-if="status !== 'pending'" class="flex justify-center">
        <div
          class="grid grid-cols-2 items-center gap-5 sm:grid-cols-3 md:grid-cols-3 xl:grid-cols-4 2xl:xl:grid-cols-5"
          :class="{
            'h-[125px] overflow-hidden md:h-[150px] lg:h-[200px]': !open,
          }"
        >
          <div
            v-for="(image, i) in images.items"
            :key="image.nid"
            class="gallery-item flex w-full items-center justify-center"
          >
            <div
              class="gallery-carousel-slide relative inline-block cursor-pointer after:absolute after:left-0 after:top-0 after:box-border after:block after:h-full after:w-full after:bg-[50%] after:bg-no-repeat after:opacity-0 after:transition-opacity after:content-['']"
              @click="() => showImg(i)"
            >
              <!-- TODO: Add height and width to avoid CLS -->
              <div class="aspect-gallery h-[125px] md:h-[150px] lg:h-[200px]">
                <img
                  class="lazy-img gallery-image mx-auto block h-full w-auto object-cover"
                  :data-srcset="getImageSrcSet(image.url)"
                  :src="getResourceUrl(image.url + '/576x576/FIT')"
                  :alt="image.metaAlt"
                  :title="image.metaTitle"
                />
              </div>
            </div>
          </div>
          <div class="padding-item" />
          <UiLightbox
            v-model="visible"
            :index="index"
            :images="
              images.items.map((image) =>
                getResourceUrl(image.url + '/2000x2000/FIT')
              )
            "
          />
        </div>
      </div>
      <div class="flex justify-end pr-6 pt-5">
        <UiPagination
          v-show="open && images.pageCount > 1"
          v-model:value="page"
          :items-per-page="itemsPerPage"
          :item-count="images.itemCount"
          :loading="status === 'pending'"
        />
      </div>
      <div
        class="flex w-full justify-center pt-16"
        :class="{ 'sticky bottom-5': open }"
      >
        <div class="cursor-pointer pb-1" @click="toggleOpen">
          <template v-if="!open">
            <UiAnimatedUnderlineText :loading="!!initialPaginationLoad">
              {{ $t('productline.gallery.cta') }}
            </UiAnimatedUnderlineText>
          </template>
          <template v-else>
            <div
              class="group flex items-center gap-x-2 rounded-full bg-neutral-100 p-3 transition-all hover:bg-neutral-200"
            >
              <Icon
                name="fa6-solid:chevron-up"
                class="ml-1 transition-all group-hover:mb-1"
                size="15"
              />
              <div>
                {{ $t('base.products.hideGallery') }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </UiSection>
</template>

<style scoped>
.gallery-item:hover .gallery-carousel-slide:after {
  opacity: 1 !important;
}

.gallery-item:hover .gallery-carousel-slide img {
  opacity: 0.4;
}

.gallery-carousel-slide:after {
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='64' height='64' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='%23fff' d='M60 32.5H4m56 0H4m56 0H4M31.5 60V4m0 56V4m0 56V4'/%3E%3C/svg%3E");
  background-size: 64px 64px;
}

.padding-item {
  flex-grow: 1;
}
</style>
